/**
 * ThemeProvider — applique `data-theme="dark|light"` au `<html>`.
 *
 * @remarks
 * Lit `useThemeStore` pour la préférence utilisateur. Pour le mode `'system'`,
 * écoute `prefers-color-scheme` via `matchMedia`.
 *
 * Les overrides CSS dans `tokens.css` sous `[data-theme="dark"]` font le reste.
 */

import { useEffect, type ReactNode } from 'react';
import { useThemeStore } from '@/app/stores/use-theme-store';

function resolveTheme(theme: 'light' | 'dark' | 'system'): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    const applied = resolveTheme(theme);
    document.documentElement.setAttribute('data-theme', applied);

    if (theme !== 'system') return;

    // Mode 'system' : suivre les changements OS
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', handler);
    return () => {
      media.removeEventListener('change', handler);
    };
  }, [theme]);

  return <>{children}</>;
}
